import { useEffect, useRef, useState } from "react";
import type { Headline as HeadlineCopy } from "../words";

/**
 * The one thing this screen says.
 *
 * It animates only when the *value* changes — an estimate that breathes
 * as traffic moves is motion that carries meaning; a number that pulses on
 * a timer is decoration, and this product does not have any.
 */
export function Headline({ copy }: { copy: HeadlineCopy }): JSX.Element {
  const [pulse, setPulse] = useState(false);
  const previous = useRef(copy.value);

  useEffect(() => {
    if (previous.current === copy.value) return;
    previous.current = copy.value;
    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 460);
    return () => clearTimeout(timer);
  }, [copy.value]);

  const toneClass = copy.tone === "halt"
    ? " headline--halt"
    : copy.tone === "settled"
      ? " headline--settled"
      : "";

  // The 96px size in the type scale exists for one thing: the number the
  // page was opened for. "Waiting to begin" and "Not available here" are
  // sentences standing in for a number nobody can give, and setting a
  // sentence that large spends the loudest voice in the system on
  // something that is not the answer.
  const isNumber = /\d/u.test(copy.value);

  return (
    <div className={`headline${toneClass}`}>
      <p className="headline__lede">{copy.lede}</p>
      <p
        className={`headline__time${isNumber ? "" : " headline__time--words"}${copy.tone === "muted" ? " headline__time--muted" : ""}${pulse ? " breathe" : ""}`}
        // The whole claim in one place for a screen reader, so it is not
        // pieced together from three separate elements read out of order.
        aria-live="polite"
      >
        {copy.value}
        {copy.unit ? <span className="headline__unit">{copy.unit}</span> : null}
      </p>
      {copy.sub ? <p className="headline__sub">{copy.sub}</p> : null}
    </div>
  );
}
